<template>
  <div
    v-if="isLoyaltyEnabled && storeAuth.isLoggedIn"
    class="loyalty-section p-5 bg-transparent rounded-lg mb-4"
    data-testid="loyalty-section"
  >
    <!-- Заголовок-раскрывашка (как в промокоде/комментарии).
         При активном ограничении списания раскрывать нечего — шеврон прячем. -->
    <div
      class="grid grid-cols-[24px_1fr_auto] gap-4 items-center"
      :class="isRedeemBlocked ? 'cursor-default' : 'cursor-pointer'"
      data-testid="loyalty-toggle"
      @click="toggleLoyalty"
    >
      <UIcon name="i-ep-coin" class="w-6 h-6" />
      <div>
        <p class="font-medium">Баллы лояльности</p>
        <p class="text-sm text-gray-500 dark:text-gray-400">Доступно: {{ availablePoints }} баллов</p>
        <!-- Иначе цифра «Доступно» молча расходится с балансом в профиле -->
        <p
          v-if="lockedPoints > 0"
          class="text-xs text-gray-400 dark:text-gray-500"
          data-testid="loyalty-locked-points"
        >
          Ещё {{ lockedPoints }} ждут оплаты прошлых заказов
        </p>
      </div>
      <UIcon
        v-if="!isRedeemBlocked"
        name="i-heroicons-chevron-right-20-solid"
        class="w-5 h-5 transition-all ease-in-out duration-300"
        :class="{ 'transform rotate-90': isUseLoyaltyPoints }"
      />
    </div>

    <!-- Списание запрещено настройками заведения: объясняем гостю, а не молчим -->
    <div
      v-if="isRedeemBlocked"
      class="mt-3 text-sm rounded-lg px-3 py-2 bg-gray-100 dark:bg-gray-800/60 text-gray-600 dark:text-gray-300"
      data-testid="loyalty-redeem-restriction"
    >
      {{ redeemRestrictionText }}
    </div>

    <!-- Взаимоисключение: баллы ИЛИ приветственная скидка на первые заказы.
         Пользователь выбирает одно — списать баллы или получить скидку.
         Текст привязан к РЕАЛЬНОМУ списанию баллов (pointsToUse > 0), а не к раскрытию
         блока: пока баллы не списываются, скидка действует и подсказка это отражает. -->
    <div
      v-if="!isRedeemBlocked && onboardingAvailable && onboardingDiscount > 0"
      :class="
        isSpendingPoints
          ? 'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 mt-3 text-sm rounded-lg px-3 py-2'
          : ''
      "
      data-testid="loyalty-onboarding-hint"
    >
      <template v-if="isSpendingPoints">
        Приветственная скидка −{{ onboardingDiscount }} ₽ не действует при списании баллов. Выключите баллы, чтобы
        получить скидку.
      </template>
    </div>

    <!-- Форма ввода баллов -->
    <div v-if="isUseLoyaltyPoints && !isRedeemBlocked" class="mt-3">
      <div class="flex items-stretch gap-3 mb-2">
        <UiKitInput
          v-model="pointsAmount"
          type="number"
          placeholder="Сколько баллов использовать"
          class="flex-1 min-w-0"
          data-testid="loyalty-points-input"
          @input="handlePointsInput"
        />
        <UiKitButton
          color="success"
          size="md"
          class="h-11 w-32 shrink-0"
          data-testid="loyalty-max"
          @click="handleSetMaxPoints"
        >
          Максимум
        </UiKitButton>
      </div>

      <!-- Информация о лимитах -->
      <div class="text-xs text-gray-500 dark:text-gray-400" data-testid="loyalty-max-hint">
        {{ maxHint }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useAuthStore } from '~/features/auth';

interface Props {
  isLoyaltyEnabled: boolean;
  availablePoints: number;
  maxAllowedPoints: number;
  useLoyaltyPoints: boolean;
  pointsToUse: number;
  /** Доступна ли пользователю приветственная скидка на первые заказы. */
  onboardingAvailable?: boolean;
  /** Размер приветственной скидки в рублях (для подсказки выбора). */
  onboardingDiscount?: number;
  /**
   * Ограничение на списание баллов из настроек заведения (мин. сумма заказа /
   * «списание с N-го заказа»). null — списание разрешено.
   */
  redeemRestriction?: {
    reason: 'min_order_amount' | 'min_paid_orders';
    minOrderAmount: number;
    fromOrderNumber: number;
  } | null;
  /** Какую долю заказа заведение разрешает оплатить баллами (доля 0..1). */
  maxRedeemPercent?: number;
  /**
   * Баллы, которые нельзя потратить сейчас: их держат неоплаченные заказы.
   * Без этой цифры «Доступно» в корзине необъяснимо меньше баланса в профиле.
   */
  lockedPoints?: number;
}

interface Emits {
  (e: 'update:useLoyaltyPoints', value: boolean): void;
  (e: 'update:pointsToUse', value: number): void;
  (e: 'pointsInput'): void;
  (e: 'setMaxPoints'): void;
  (e: 'toggleLoyalty', value: boolean): void;
}
const props = defineProps<Props>();
const emit = defineEmits<Emits>();
const storeAuth = useAuthStore();
/** Реальный лимит заведения, а не константа: у разных заведений он разный (50%, 30%). */
const maxPercentLabel = computed(() => {
  const percent = (props.maxRedeemPercent ?? 0.5) * 100;
  return `${Math.round(percent * 10) / 10}%`;
});

const lockedPoints = computed(() => Math.max(0, props.lockedPoints ?? 0));

/**
 * Подпись к максимуму.
 *
 * Максимум упирается в две разные вещи: долю заказа и остаток баллов. Раньше подпись
 * всегда объясняла его процентом — гость с 81 баллом на заказе 1550 ₽ читал
 * «Максимум: 81 (50% от суммы)», хотя 50% — это 775, а ограничил его остаток.
 */
const maxHint = computed(() => {
  const cappedByBalance = props.maxAllowedPoints >= props.availablePoints;
  return cappedByBalance
    ? `Максимум: ${props.maxAllowedPoints} — все доступные баллы`
    : `Максимум: ${props.maxAllowedPoints} (${maxPercentLabel.value} от суммы)`;
});

/** Списание запрещено настройками заведения — форму ввода не показываем. */
const isRedeemBlocked = computed(() => Boolean(props.redeemRestriction));

/** Человеческое объяснение, почему баллы сейчас не списать. */
const redeemRestrictionText = computed(() => {
  const restriction = props.redeemRestriction;
  if (!restriction) return '';

  if (restriction.reason === 'min_order_amount') {
    return `Баллами можно оплатить заказ от ${restriction.minOrderAmount} ₽. Добавьте товаров, чтобы списать баллы.`;
  }

  return restriction.fromOrderNumber === 2
    ? 'Баллы можно списать начиная со второго заказа. Сейчас они копятся.'
    : `Баллы можно списать начиная с ${restriction.fromOrderNumber}-го заказа. Сейчас они копятся.`;
});

/**
 * Реактивная ссылка на использование баллов лояльности
 */
const isUseLoyaltyPoints = computed({
  get: () => props.useLoyaltyPoints,
  set: (value: boolean) => emit('update:useLoyaltyPoints', value),
});

/**
 * Баллы РЕАЛЬНО списываются (а не просто раскрыт блок): только тогда приветственная
 * скидка действительно не применяется. Пока pointsToUse = 0 — скидка действует.
 */
const isSpendingPoints = computed(() => props.useLoyaltyPoints && props.pointsToUse > 0);

/**
 * Реактивная ссылка на количество баллов
 */
const pointsAmount = computed<number>({
  get: () => props.pointsToUse,
  // UiKitInput(type="number") отдаёт null при пустом поле — приводим к 0
  set: (value: number | null) => emit('update:pointsToUse', Number(value) || 0),
});

/**
 * Обрабатывает ввод количества баллов
 */
function handlePointsInput(): void {
  emit('pointsInput');
}

/**
 * Устанавливает максимальное количество баллов
 */
function handleSetMaxPoints(): void {
  emit('setMaxPoints');
}

/**
 * Разворачивает/сворачивает блок баллов.
 * Раскрытие = «списываю баллы» (взаимоисключение с приветственной скидкой),
 * сворачивание = «получаю скидку на первые заказы».
 */
function toggleLoyalty(): void {
  // Списание запрещено настройками — раскрывать форму нечему.
  if (isRedeemBlocked.value) return;

  const next = !isUseLoyaltyPoints.value;
  isUseLoyaltyPoints.value = next;
  emit('toggleLoyalty', next);
}
</script>

<style scoped lang="scss">
.loyalty-section {
  @apply p-5 bg-transparent rounded-lg mb-4;
}
</style>